import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { loadOrganisationSettings } from '../../../utils/organisationSettings';
import './PersonnelPage.scss';

const StatusBadge = ({ active }) => (
  <span className={`pers-page__badge pers-page__badge--${active ? 'active' : 'inactive'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const ClassBadge = ({ value, types }) => {
  const key = value === 'consultant' ? 'consultant' : 'employee';
  const label = (types || []).find((row) => row.code === value)?.label
    || (value === 'consultant' ? 'Consultant' : value === 'employee' ? 'Employee' : value);
  return <span className={`pers-page__badge pers-page__badge--${key}`}>{label}</span>;
};

const PersonnelPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [types, setTypes] = useState([
    { code: 'employee', label: 'Employee' },
    { code: 'consultant', label: 'Consultant' }
  ]);

  useDocumentTitle('Personnel');

  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const list = data?.settings?.personnelTypes;
      if (list?.length) setTypes(list);
    });
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const [peopleRes, proposalRes] = await Promise.all([
        parivartanAPI.listPersonnel(),
        parivartanAPI.listPersonnelProposals().catch(() => ({ data: { data: { proposals: [] } } }))
      ]);
      setRows(peopleRes.data?.data?.personnel || []);
      setProposals(proposalRes.data?.data?.proposals || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load personnel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const admin = useAdminRecordDelete({
    entity: 'personnel',
    noun: 'person',
    plural: 'people',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'personnel' });

  const tableRows = useMemo(
    () => rows.map((person) => {
      const sectors = Array.isArray(person.sectorSpecialisation)
        ? person.sectorSpecialisation.filter(Boolean)
        : [];
      const onTenders = proposals.filter((item) => String(item.personnelId) === String(person._id)).length;
      return {
        ...person,
        sectorsLabel: sectors.length ? sectors.join(' · ') : '—',
        cvLabel: person.cvVersion || '',
        onTendersLabel: onTenders ? String(onTenders) : '—'
      };
    }),
    [rows, proposals]
  );

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Person',
        mobileTitle: true,
        render: (value) => <span className="pers-page__name">{value}</span>
      },
      { key: 'designation', label: 'Designation' },
      {
        key: 'classification',
        label: 'Type',
        width: '140px',
        render: (value) => <ClassBadge value={value} types={types} />
      },
      {
        key: 'cvLabel',
        label: 'CV version',
        width: '120px',
        render: (value) => (
          value
            ? <span className="pers-page__cv">v{value}</span>
            : <span className="pers-page__cv pers-page__cv--empty">Not set</span>
        )
      },
      {
        key: 'sectorsLabel',
        label: 'Sectors',
        render: (value) => <span className="pers-page__sectors">{value}</span>
      },
      revisionColumn,
      {
        key: 'isActive',
        label: 'Status',
        width: '120px',
        render: (value) => <StatusBadge active={value} />
      },
      { key: 'onTendersLabel', label: 'On tenders', width: '120px' }
    ],
    []
  );

  const openRecord = (row, mode) => {
    const id = row._id || row.id;
    if (!id) return;
    navigate(mode === 'edit' ? `/apps/tender/personnel/${id}/edit` : `/apps/tender/personnel/${id}`);
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="pers-page">
      <WorkspacePageHeader
        title="Personnel"
        help="personnel"
        subtitle="The firm roster. Open a person to update their profile or name them on an active tender."
        actions={(
          <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/personnel/new')}>
            New person
          </Button>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="personnel.download"
        title="Personnel"
        data={tableRows}
        columns={columns}
        searchPlaceholder="Search name, designation, sector…"
        pageSize={12}
        onView={(row) => openRecord(row, 'view')}
        onEdit={(row) => openRecord(row, 'edit')}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default PersonnelPage;
